// ManualReplyComposer — US-081 (Wave 5c Agent 5c.3, EPIC-06).
//
// Modal hosting a free-text composer for the right-rail "Reply manually"
// quick-action. Submitting POSTs to `/api/v1/conversations/:id/manual-reply`
// (see `apps/api/src/routes/conversations-context.ts`); the server marks the
// outbound message `outboundSource: 'manual_app'` and supersedes any
// pending AI draft.
//
// Validation is zod-driven (`messageDraftFormSchema` from `@barel/core`).
// We surface inline error copy + a toast on success/failure so the user
// gets feedback regardless of where their attention is anchored.
//
// Coordination: 5c.2 owns the live thread re-render — we invalidate
// `['conversation', id]` via the `useManualReply` hook so their query
// re-fetches automatically.
//
// PostHog/Sentry are no-op per Wave 5c hard rules.

import { useEffect, useRef, useState } from 'react';

import { useManualReply, type ApiError } from '@barel/api-client/react';
import { messageDraftFormSchema } from '@barel/core/schemas';
import { useT } from '@barel/i18n';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Textarea,
  toast,
} from '@barel/ui';

export interface ManualReplyComposerProps {
  open: boolean;
  onClose: () => void;
  /** Active conversation id from the inbox selection. */
  conversationId: string;
  /** Optional callback fired after a successful send (lets 5c.2 close the AI draft surface). */
  onSent?: (messageId: string) => void;
}

/** Distinct from `messageDraftFormSchema`'s shape — we only collect the body. */
function validateBody(body: string, t: (key: string) => string): string | null {
  const trimmed = body.trim();
  if (trimmed.length === 0) return t('context.manualReply.validationEmpty');
  if (trimmed.length > 4000) return t('context.manualReply.validationTooLong');
  // Re-run zod against the canonical schema to keep validation rules
  // in lockstep with the API contract.
  const parsed = messageDraftFormSchema.safeParse({ body: trimmed, attachments: [] });
  if (!parsed.success) return t('context.manualReply.validationEmpty');
  return null;
}

export function ManualReplyComposer({
  open,
  onClose,
  conversationId,
  onSent,
}: ManualReplyComposerProps) {
  const t = useT('inbox');
  const mutation = useManualReply();

  const [body, setBody] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  // SPEC-SEND-TRANSPORT §1.4 idempotency — a `clientToken` is generated once
  // per logical reply (keyed off the body being sent) and reused on every
  // retry of that SAME reply, so a lost response can't double-send. Editing
  // the body naturally mints a fresh token below (same pattern as
  // ThreadPanel's `sendTokenRef`).
  const sendTokenRef = useRef<{ body: string; token: string } | null>(null);

  // Reset whenever the modal opens against a fresh conversation.
  useEffect(() => {
    if (open) {
      setBody('');
      setValidation(null);
      sendTokenRef.current = null;
    }
  }, [open, conversationId]);

  const submitting = mutation.isPending;

  const handleSubmit = async (): Promise<void> => {
    const error = validateBody(body, t as (key: string) => string);
    if (error !== null) {
      setValidation(error);
      return;
    }
    setValidation(null);
    const trimmed = body.trim();
    if (sendTokenRef.current?.body !== trimmed) {
      sendTokenRef.current = { body: trimmed, token: crypto.randomUUID() };
    }
    try {
      const result = await mutation.mutateAsync({
        conversationId,
        body: trimmed,
        clientToken: sendTokenRef.current.token,
      });
      toast.success({ title: t('context.manualReply.successToast') });
      onSent?.(result.message.id);
      onClose();
    } catch (cause) {
      const apiError = cause as ApiError;
      toast.error({
        title: t('context.manualReply.errorToast'),
        description: apiError?.message,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent size="md" data-testid="manual-reply-modal">
        <DialogHeader
          title={t('context.manualReply.title')}
          description={t('context.manualReply.description')}
          onClose={onClose}
        />
        <DialogBody>
          <Textarea
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              if (validation !== null) setValidation(null);
            }}
            placeholder={t('context.manualReply.placeholder')}
            rows={6}
            disabled={submitting}
            aria-label={t('context.manualReply.title')}
            data-testid="manual-reply-textarea"
          />
          {validation !== null ? (
            <p
              role="alert"
              className="mt-2 text-xs text-error"
              data-testid="manual-reply-validation"
            >
              {validation}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" size="md" onClick={onClose} disabled={submitting}>
            {t('context.manualReply.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={body.trim().length === 0}
            loading={submitting}
            data-testid="manual-reply-send"
          >
            {submitting ? t('context.manualReply.sending') : t('context.manualReply.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
